/**
 * Mappings list page — Mapping v2.
 *
 * One row per Mapping (a reusable typed function). Each opens a
 * detail page for editing signature / strategy / rows. Create dialog
 * spawns a new mapping; row data is added on the detail page.
 *
 * The old per-row K/V table view was retired with Mapping v2 (see
 * migration 20260527_0033). Rows of a `manual_table` mapping now
 * live under `/mappings/{id}` in their own table.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, FunctionSquare, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { MappingCreateDialog } from "@/components/mappings/MappingDialog";
import { Badge } from "@/components/ui/badge";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  mappingsApi,
  STRATEGY_META,
  type MappingSummary,
  type StrategyKind,
} from "@/lib/api-mappings";
import { usePaginationState } from "@/lib/pagination";
import { usePermissions } from "@/lib/permissions";

const STRATEGY_TONE: Record<StrategyKind, "active" | "neutral" | "warning"> = {
  manual_table: "active",
  db_lookup: "neutral",
  http_lookup: "neutral",
};

export function MappingsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can("mappings", "write");
  const canDelete = can("mappings", "delete");
  const [createOpen, setCreateOpen] = React.useState(false);
  const { page, pageSize, setPage, setPageSize } = usePaginationState();

  const query = useQuery({
    queryKey: ["mappings", page, pageSize],
    queryFn: () => mappingsApi.list({ page, page_size: pageSize }),
  });

  const items: MappingSummary[] = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => mappingsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mappings"] }),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Mappings"
        description="Reusable functions with a typed signature. Call them from workflow nodes to translate codes, look up canonical ids, enrich payloads — without duplicating logic."
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New mapping
            </Button>
          )
        }
      />
      <PageContent>
        {items.length === 0 && !query.isLoading ? (
          <EmptyState
            icon={FunctionSquare}
            title="No mappings yet"
            description="Create one to declare a reusable lookup: input fields → output fields, with a strategy that resolves them. The simplest is `manual_table` — you maintain the rows in the UI."
            action={
              canWrite ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Create the first mapping
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Strategy</Th>
                  <Th className="w-32">Signature</Th>
                  <Th className="w-20 text-right">Rows</Th>
                  <Th className="w-24">Status</Th>
                  <Th className="w-20 text-right" />
                </Tr>
              </Thead>
              <Tbody>
                {items.map((m) => (
                  <Tr key={m.id}>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <Link
                          to={`/mappings/${m.id}`}
                          className="font-medium text-foreground hover:text-accent"
                        >
                          {m.name}
                        </Link>
                        <DescriptionTooltip text={m.description} />
                      </div>
                    </Td>
                    <Td>
                      <Badge tone={STRATEGY_TONE[m.strategy_kind]} className="text-[10px]">
                        {STRATEGY_META[m.strategy_kind].label}
                      </Badge>
                      {!STRATEGY_META[m.strategy_kind].available && (
                        <span className="ml-2 text-[10px] italic text-muted-foreground">
                          resolver pending
                        </span>
                      )}
                    </Td>
                    <Td className="text-xs text-muted-foreground">
                      <span className="font-mono">
                        {m.inputs.length}→{m.outputs.length}
                      </span>
                      {m.signature_locked && (
                        <span
                          className="ml-2 text-[10px] uppercase tracking-wide text-warning"
                          title="Signature is locked because a workflow uses it. Only nullable-output additions are allowed."
                        >
                          locked
                        </span>
                      )}
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {m.strategy_kind === "manual_table" ? m.row_count : "—"}
                    </Td>
                    <Td>
                      {m.enabled ? (
                        <Badge tone="active">enabled</Badge>
                      ) : (
                        <Badge tone="neutral">disabled</Badge>
                      )}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete mapping "${m.name}"? This deletes all rows too and cannot be undone.`,
                                )
                              ) {
                                deleteMutation.mutate(m.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            title={`Delete "${m.name}"`}
                            aria-label="Delete mapping"
                            className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-danger disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </button>
                        )}
                        <Link
                          to={`/mappings/${m.id}`}
                          className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-accent"
                          title="Open mapping"
                        >
                          <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                        </Link>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </PageContent>

      <MappingCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(m) => navigate(`/mappings/${m.id}`)}
      />
    </PageContainer>
  );
}
